/**
 * /accounting/ar — Collections Workbench.
 *
 * Aging buckets, customer-level open balances, dunning recommendations.
 * Read-only against GL; closes the working-capital loop with AP.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Input } from "@/components/ui/input";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useSeedGeneralLedger } from "@/data/accounting/useSeedGeneralLedger";
import {
  getARAgingSummary, getCustomerARList, recommendDunning,
  AGING_BUCKETS, type AgingBucket,
} from "@/data/accounting/ar/arAging";

const fmt = (n: number) =>
  `${n < 0 ? "-" : ""}$${Math.abs(Math.round(n)).toLocaleString()}`;

const BUCKET_LABEL: Record<AgingBucket, string> = {
  current: "Current",
  "1-30": "1–30",
  "31-60": "31–60",
  "61-90": "61–90",
  "90+": "90+",
};

const BUCKET_TONE: Record<AgingBucket, string> = {
  current: "",
  "1-30": "",
  "31-60": "text-amber-600 dark:text-amber-500",
  "61-90": "text-amber-600 dark:text-amber-500",
  "90+": "text-destructive",
};

export default function ARCollectionsHubPage() {
  useSeedGeneralLedger();
  const { journalEntries } = useGeneralLedger();
  const [search, setSearch] = useState("");
  const [bucketFilter, setBucketFilter] = useState<AgingBucket | "all">("all");

  const summary = useMemo(() => getARAgingSummary(journalEntries), [journalEntries]);
  const customers = useMemo(() => getCustomerARList(journalEntries), [journalEntries]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return customers.filter(c => {
      if (q && !c.customerLabel.toLowerCase().includes(q)) return false;
      if (bucketFilter !== "all" && c.bucketTotals[bucketFilter] <= 0) return false;
      return true;
    });
  }, [customers, search, bucketFilter]);

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6">
        <Link to="/accounting/gl" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="h-3 w-3" /> Back to General Ledger
        </Link>
        <h1 data-tour-id="ar-hub-table" className="text-2xl font-semibold tracking-tight">AR Collections</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Live from the 1100 Accounts Receivable subledger. Aging as of {summary.asOf}.
        </p>
      </div>

      {/* Aging summary */}
      <CockpitSection>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Kpi label="Total open" value={fmt(summary.totalOpen)} sub={`${summary.customerCount} customers`} />
          {AGING_BUCKETS.map(b => (
            <Kpi
              key={b}
              label={BUCKET_LABEL[b]}
              value={fmt(summary.bucketTotals[b])}
              sub={`${summary.bucketCustomerCount[b]} cust`}
              tone={BUCKET_TONE[b]}
            />
          ))}
        </div>
        {summary.totalUnappliedCredit > 0 && (
          <div className="mt-3 rounded-md border border-sky-500/30 bg-sky-500/5 p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-sky-600 dark:text-sky-400" />
            <div>
              <div className="font-medium">{fmt(summary.totalUnappliedCredit)} in unapplied credit</div>
              <div className="text-xs text-muted-foreground">
                {summary.unappliedCreditCustomerCount} customer{summary.unappliedCreditCustomerCount === 1 ? "" : "s"} have prepayments or overpayments on file. Apply to the next invoice or refund.
              </div>
            </div>
          </div>
        )}
        {summary.overdueAmount > 0 && (
          <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-500" />
            <div>
              <div className="font-medium">{fmt(summary.overdueAmount)} past 30 days</div>
              <div className="text-xs text-muted-foreground">
                Oldest open invoice is {summary.oldestDaysOpen} days. Triage below.
              </div>
            </div>
          </div>
        )}
      </CockpitSection>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search customers…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <div className="flex items-center gap-1">
          {(["all", ...AGING_BUCKETS] as Array<"all" | AgingBucket>).map(b => {
            const active = bucketFilter === b;
            return (
              <button
                key={b}
                onClick={() => setBucketFilter(b)}
                className={
                  "text-xs px-2.5 py-1 rounded-sm border transition-colors " +
                  (active
                    ? "bg-primary/15 text-primary border-primary/30 ring-1 ring-primary/30"
                    : "border-border hover:bg-accent/40")
                }
              >
                {b === "all" ? "All" : BUCKET_LABEL[b]}
              </button>
            );
          })}
        </div>
      </div>

      <CockpitSection>
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-right px-3 py-2">Current</th>
                <th className="text-right px-3 py-2">1–30</th>
                <th className="text-right px-3 py-2">31–60</th>
                <th className="text-right px-3 py-2">61–90</th>
                <th className="text-right px-3 py-2">90+</th>
                <th className="text-right px-3 py-2">Open</th>
                <th className="text-left px-3 py-2">Oldest</th>
                <th className="text-left px-3 py-2">Next action</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No open AR matching this filter.
                  </td>
                </tr>
              )}
              {visible.map(c => {
                const d = recommendDunning(c);
                return (
                  <tr key={c.customerId} className="border-t border-border hover:bg-accent/30">
                    <td className="px-3 py-2 font-medium">
                      <Link to={`/accounting/ar/${encodeURIComponent(c.customerId)}`} className="hover:underline">
                        {c.customerLabel}
                      </Link>
                    </td>
                    {AGING_BUCKETS.map(b => (
                      <td key={b} className={`px-3 py-2 text-right tabular-nums ${c.bucketTotals[b] > 0 ? BUCKET_TONE[b] : "text-muted-foreground/50"}`}>
                        {c.bucketTotals[b] > 0 ? fmt(c.bucketTotals[b]) : "—"}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(c.totalOpen)}</td>
                    <td className={`px-3 py-2 text-xs ${c.oldestDaysOpen > 60 ? "text-destructive" : c.oldestDaysOpen > 30 ? "text-amber-600" : "text-muted-foreground"}`}>
                      {c.oldestDaysOpen}d
                    </td>
                    <td className="px-3 py-2">
                      <span className={
                        "text-xs px-1.5 py-0.5 rounded-sm " +
                        (d.tone === "critical" ? "bg-destructive/15 text-destructive" :
                         d.tone === "warn" ? "bg-amber-500/15 text-amber-700 dark:text-amber-400" :
                         "bg-muted text-muted-foreground")
                      }>
                        {d.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CockpitSection>
    </CockpitPage>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${tone ?? ""}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
